import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  ts: string; // ISO timestamp
  format?: (ts: string, t: TFunction, lang: string, showTime?: boolean) => string;
  render: (time: string) => JSX.Element | null;
  showTime?: boolean;
};

export function RelativeTime({ ts, format = DEFAULT_FORMAT_TIME, render, showTime = true }: Props) {
  const { t, i18n } = useTranslation();
  const [time, setTime] = useState(format(ts, t, i18n.language, showTime));

  useEffect(() => {
    setTime(format(ts, t, i18n.language, showTime));

    const interval = setInterval(() => {
      setTime(format(ts, t, i18n.language, showTime));
    }, 15000);

    return () => clearInterval(interval);
  }, [format, setTime, ts, t, i18n.language, showTime]);

  return render(time);
}

const DEFAULT_FORMAT_TIME = (
  timestamp: string,
  t: TFunction,
  lang: string,
  showTime = true,
): string => {
  const ts = DateTime.fromISO(timestamp).setLocale(lang);

  if (ts.diffNow('minutes').minutes > -1) {
    return t('lessThanMinuteAgo');
  }

  if (ts.diffNow('days').days > -1) {
    return ts.toRelative() ?? '';
  }
  return showTime ? ts.toFormat("DD 'at' h:mm a") : ts.toFormat('dd LLL yyyy');
};

export const RELATIVE_FORMAT_TIME = (timestamp: string, t: TFunction, lang: string): string => {
  const ts = DateTime.fromISO(timestamp).setLocale(lang);

  if (ts.diffNow('minutes').minutes > -1) {
    return t('lessThanMinuteAgo');
  }

  if (ts.diffNow('hours').hours > -1) {
    return ts.toRelative({ unit: 'minutes' }) ?? '';
  }

  if (ts.diffNow('days').days > -1) {
    return ts.toRelative({ unit: 'hours' }) ?? '';
  }

  return ts.toRelative({ unit: 'days' }) ?? '';
};

export const SHORT_FORMAT_TIME = (timestamp: string, t: TFunction, lang: string): string => {
  const ts = DateTime.fromISO(timestamp).setLocale(lang);

  return (
    ts
      .toRelative()
      ?.replace(/ days?/, 'd')
      .replace(/ hours?/, 'h')
      .replace(/ minutes?/, 'min')
      .replace(/ seconds?/, 's') ?? ''
  );
};
